/**
 * Helpers for normalising language / country chip data before it reaches Alpine x-for.
 *
 * Alpine's x-for reconciles by :key, and the key set must be unique and defined for every
 * iteration. Duplicate keys collide in Alpine's internal lookup map. Undefined keys collapse
 * every item into the same bucket. Either pattern breaks the lookup phase and crashes
 * reconciliation when Alpine tries to call .after(marker) on the resulting undefined element.
 *
 * Server-rendered chip data can arrive as a mix of strings, objects with .code, or values
 * missing .code entirely. Codes themselves can show up in any case. normalizeCode and
 * normalizeChipItems fold all of that into a single canonical {code, name} shape and drop
 * anything that cannot yield a usable code.
 */

/**
 * Canonicalise a code-ish value to UPPERCASE with surrounding whitespace trimmed.
 *
 * Accepts strings only. Numbers, booleans, objects and arrays are rejected because chip
 * codes are always ISO language / country strings — coercing other types would let
 * `true` become `'TRUE'` or `{}` become `'[OBJECT OBJECT]'` and then enter the chip
 * list as a real :key.
 *
 * @returns {string|null} the canonical code, or null if no usable code can be derived
 */
export function normalizeCode(raw) {
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    return trimmed.toUpperCase();
}

/**
 * Build a name-lookup map keyed by canonical (UPPERCASE) code. The lookup the caller
 * supplies may have raw server-cased keys, so canonicalise both sides of the comparison.
 * Non-string values and non-canonicalisable keys are skipped.
 */
function buildNameLookup(allMap) {
    if (!allMap || typeof allMap !== 'object') return {};
    const out = {};
    for (const rawKey of Object.keys(allMap)) {
        const canonical = normalizeCode(rawKey);
        if (!canonical) continue;
        const value = allMap[rawKey];
        if (typeof value !== 'string') continue;
        // First occurrence of any canonical key wins, mirroring the dedup rule below.
        if (!(canonical in out)) out[canonical] = value;
    }
    return out;
}

/**
 * Normalise an array of chip items (strings or {code, name} objects) into a deduped list
 * of { code, name } records. First occurrence of any canonical code wins, regardless of
 * whether it came in as a string or as an object: a `[{code: 'en', name: 'Custom'}, 'en']`
 * input keeps the object's custom name. Items without a string-typed, non-empty code are
 * dropped. The allMap lookup itself is canonicalised, so a `{en: 'English'}` map will still
 * supply a name for a chip whose canonical code is `'EN'`.
 *
 * @param {unknown}                  items   raw items as rendered by the server
 * @param {Object<string,string>|null|undefined} [allMap]
 *        optional name lookup; keys may be in any case
 * @returns {Array<{code: string, name: string}>}
 */
export function normalizeChipItems(items, allMap) {
    if (!Array.isArray(items)) return [];
    const lookup = buildNameLookup(allMap);
    const seen = new Set();
    const out = [];
    for (const raw of items) {
        let code;
        let nameFromRaw;
        if (raw && typeof raw === 'object') {
            code = normalizeCode(raw.code);
            if (typeof raw.name === 'string' && raw.name.length > 0) {
                nameFromRaw = raw.name;
            }
        } else {
            code = normalizeCode(raw);
        }
        if (!code || seen.has(code)) continue;
        seen.add(code);
        out.push({ code, name: nameFromRaw || lookup[code] || code });
    }
    return out;
}
